<template>
  <div class="po-stkchk">
    <header class="query-header" aria-label="质检单">
      <button type="button" class="back-button" aria-label="返回功能首页" :disabled="busy" @click="onClickLeft">
        <van-icon name="arrow-left" size="20" aria-hidden="true" />
      </button>
      <form class="search-form" role="search" @submit.prevent="onSearch">
        <div class="search-input">
          <van-icon name="search" size="14" aria-hidden="true" />
          <input v-model="search" :disabled="busy" aria-label="搜索采购单号、质检单号、供应商或物料编码"
            placeholder="采购单 / 质检单 / 供应商 / 物料" type="search" enterkeyhint="search"
            @input="resetSearchEnter" @blur="resetSearchEnter"
            @keydown.enter.prevent="onSearchEnter" @keyup.enter.prevent="onSearchEnter" />
          <button v-if="search" type="button" class="clear-button" aria-label="清空搜索" @click="clearSearch">
            <van-icon name="cross" size="16" />
          </button>
        </div>
        <button type="submit" class="header-action-button" aria-label="搜索" :disabled="!permissions.canView || busy">
          <van-icon name="search" size="16" aria-hidden="true" />
        </button>
        <button type="button" class="header-action-button" aria-label="刷新" :disabled="listLoading || busy || !permissions.canView" @click="loadDocuments(true)">
          <van-icon name="replay" size="16" :class="{ 'is-spinning': listLoading }" aria-hidden="true" />
        </button>
      </form>
    </header>

    <main id="qa-document-list" ref="listContent" class="list-content" :aria-busy="listLoading">
      <div v-if="permissionLoading" class="state-panel" role="status"><van-loading size="24px" />正在读取权限…</div>
      <div v-else-if="permissionError" class="state-panel" role="alert">
        <p>{{ permissionError }}</p><van-button class="primary-button" @click="initialize">重试</van-button>
      </div>
      <div v-else-if="!permissions.canView" class="state-panel">当前账号没有来料质检单查看权限</div>
      <template v-else>
        <div v-if="listError" class="state-panel" role="alert">
          <p>{{ listError }}</p><van-button class="primary-button" @click="onSearch">重新查询</van-button>
        </div>
        <div v-else-if="listLoading && !allDocuments.length" class="state-panel" role="status"><van-loading size="24px" />正在查询质检单…</div>
        <div v-else-if="!allDocuments.length" class="state-panel">
          <van-icon name="description" size="40" color="#999999" /><p>暂无待质检单据</p>
        </div>
        <template v-else>
          <div v-if="summaryError" class="inline-notice" role="alert">
            <span>物料摘要读取失败：{{ summaryError }}</span><button type="button" @click="loadSummaries">重试</button>
          </div>
          <article v-for="item in allDocuments" :key="item.IDSEQ" class="qa-card" :aria-label="'质检单 ' + item.IDNO">
            <header class="card-header">
              <h2>{{ item.IDNO }}</h2><span class="status-tag is-pending">待质检</span>
            </header>
            <dl class="document-info">
              <div><dt>质检日期</dt><dd>{{ item.ICDTTM || '—' }}</dd></div>
              <div><dt>单据类型</dt><dd>来料质检单</dd></div>
              <div class="full-row"><dt>送货单位</dt><dd>{{ item.SUPNM || '—' }}</dd></div>
              <div class="full-row"><dt>采购单号</dt><dd>{{ purchaseNumbers(item) }}</dd></div>
            </dl>
            <div v-if="summaryLoading && !detailsFor(item).length" class="summary-state">物料信息加载中…</div>
            <div v-else-if="!detailsFor(item).length" class="summary-state">{{ summaryError ? '物料信息暂不可用' : '暂无物料明细' }}</div>
            <dl class="quantity-grid document-quantities">
              <div><dt>送货数量</dt><dd>{{ summaryQuantity(item, 'ACTQTY') }}</dd></div>
              <div><dt>质检不良数</dt><dd>{{ summaryQuantity(item, 'QCQTY') }}</dd></div>
              <div><dt>质检合格数</dt><dd>{{ summaryQuantity(item, 'STKQTY') }}</dd></div>
            </dl>
            <p v-if="item.CHKUSER || item.CHKDTTM" class="audit-info">审核用户：{{ item.CHKUSER || '—' }} {{ item.CHKDTTM || '' }}</p>

            <footer v-if="!isExpanded(item)" class="card-footer">
              <p class="detail-count">共 {{ detailsFor(item).length }} 项明细</p>
              <div class="card-actions">
                <van-button size="small" class="confirm-button" aria-label="质检" :disabled="cfmBool || busy"
                  :loading="busy && activeDocumentId === String(item.IDSEQ) && activeAction === 'Y'"
                  @click="submitAudit(item)">质检</van-button>
                <button type="button" class="toggle-expand-btn" :aria-expanded="false" @click="toggleExpand(item)">
                  <span>展开</span>
                  <van-icon name="arrow-down" />
                </button>
              </div>
            </footer>

            <!-- 就地展开的明细区域 -->
            <section v-if="isExpanded(item)" class="expanded-details" :aria-label="'质检单 ' + item.IDNO + ' 明细'">
              <div v-if="dictionaryError || dictionaryLoading" class="dictionary-notice" role="status">
                <span>{{ dictionaryLoading ? '正在读取质检结果选项…' : dictionaryError }}</span>
                <button v-if="!dictionaryLoading" type="button" @click="loadDictionary">重新读取</button>
              </div>
              <p v-if="!detailsFor(item).length" class="detail-state">暂无质检明细</p>
              <article v-for="(row, index) in detailsFor(item)" :key="rowKey(row)" class="detail-card" :aria-label="'质检物料 ' + (row.MATNM || row.MATNO)">
                <header class="line-heading">
                  <span class="line-number">{{ index + 1 }}</span>
                  <h3><span class="material-code">{{ row.MATNO || '—' }}</span><span>{{ row.MATNM || '未命名物料' }}</span></h3>
                </header>
                <p class="line-meta">{{ row.SPECNM || row.MATDESC || '暂无规格' }}</p>
                <dl class="line-info">
                  <div v-for="field in detailInformation(row)" :key="field.field" :class="{ 'wide-info': /备注|日期|时间/.test(field.title) }">
                    <dt>{{ field.title }}</dt><dd :class="{ 'quantity-value': /QTY$/.test(field.field) || /数量|不良数|合格数/.test(field.title) }">{{ displayValue(row[field.field]) }}</dd>
                  </div>
                  <div><dt>{{ fieldTitle('ACTQTY', '送货数量') }}</dt><dd class="quantity-value">{{ displayValue(row.ACTQTY) }}</dd></div>
                </dl>
                <div class="inspection-inputs">
                  <div class="result-field">
                    <label :for="'qa-result-' + item.IDSEQ + '-' + index">质检结果<span class="required"> *</span></label>
                    <select :id="'qa-result-' + item.IDSEQ + '-' + index" :value="row.QCRESULT == null ? '' : row.QCRESULT" aria-required="true"
                      :disabled="detailInputDisabled" @change="updateDetailDraft(item, row, 'QCRESULT', $event.target.value)">
                      <option value="" disabled>请选择</option>
                      <option v-for="option in dictionary.options" :key="option.CODE" :value="option.CODE">{{ option.CNAME }}</option>
                    </select>
                  </div>
                  <div class="deduction-field">
                    <label :for="'qa-quantity-' + item.IDSEQ + '-' + index">质检不良数<span class="required"> *</span></label>
                    <input :id="'qa-quantity-' + item.IDSEQ + '-' + index" class="qa-quantity-input" :value="row.QCQTY" type="number" inputmode="decimal" min="0" step="any"
                      aria-required="true" :disabled="detailInputDisabled" placeholder="不良数"
                      @input="updateDetailDraft(item, row, 'QCQTY', $event.target.value)" />
                  </div>
                </div>
                <div class="stock-preview">
                  <span>质检合格数</span>
                  <strong>{{ calculation(row).values ? calculation(row).values.STKQTY : '—' }}</strong>
                </div>
                <p v-if="calculation(row).error" class="quantity-error" role="status">{{ calculation(row).error }}</p>
                <fieldset v-if="checkFields.length" class="qa-checks">
                  <legend>检验项目及报告</legend>
                  <van-checkbox v-for="field in checkFields" :key="field.field" :value="row[field.field] === 'Y'" shape="square" checked-color="#235EFF"
                    :aria-label="field.title" :disabled="detailInputDisabled || field.disabled"
                    @keyup.space.native.prevent="toggleInlineCheck(item, row, field)" @keyup.enter.native.prevent="toggleInlineCheck(item, row, field)"
                    @input="updateDetailDraft(item, row, field.field, $event ? 'Y' : 'N')">
                    {{ field.title }}
                  </van-checkbox>
                </fieldset>
                <van-field class="remark-field" :value="row.RMK" label="备注" rows="1" autosize placeholder="填写质检说明（选填）"
                  :disabled="detailInputDisabled" @input="updateDetailDraft(item, row, 'RMK', $event)" />
                <p v-if="materialImageLoading[materialImageKey(row)]" class="material-image-status" role="status">正在读取物料图片…</p>
                <div v-else-if="materialImageErrors[materialImageKey(row)]" class="material-image-status" role="alert">
                  <span>物料图片读取失败</span><button type="button" @click="loadMaterialImages([row])">重新读取</button>
                </div>
                <div v-if="materialImages(row).length" class="material-images" role="group" :aria-label="(row.MATNO || '物料') + '图片'">
                  <button v-for="(src, imageIndex) in materialImages(row)" :key="src" type="button" class="material-thumbnail"
                    :aria-label="'查看' + (row.MATNO || '物料') + '第' + (imageIndex + 1) + '张图片'" @click="previewMaterialImages(row, imageIndex, $event)">
                    <van-image :src="src" fit="contain" :alt="(row.MATNM || row.MATNO || '物料') + '图片 ' + (imageIndex + 1)">
                      <template slot="error"><span class="material-image-error">加载失败</span></template>
                    </van-image>
                  </button>
                </div>
              </article>
              <dl v-if="detailsFor(item).length" class="detail-totals">
                <div><dt>送货数量合计</dt><dd>{{ calcDocumentTotals(item).ACTQTY }}</dd></div>
                <div><dt>质检不良数合计</dt><dd>{{ calcDocumentTotals(item).QCQTY }}</dd></div>
                <div><dt>质检合格数合计</dt><dd>{{ calcDocumentTotals(item).STKQTY }}</dd></div>
              </dl>

              <!-- 展开时置底的操作区：质检与收起按钮 -->
              <footer class="card-footer expanded-footer">
                <p class="detail-count">共 {{ detailsFor(item).length }} 项明细</p>
                <div class="card-actions">
                  <van-button size="small" class="confirm-button" aria-label="质检" :disabled="cfmBool || busy"
                    :loading="busy && activeDocumentId === String(item.IDSEQ) && activeAction === 'Y'"
                    @click="submitAudit(item)">质检</van-button>
                  <button type="button" class="toggle-expand-btn" :aria-expanded="true" @click="toggleExpand(item)">
                    <span>收起</span>
                    <van-icon name="arrow-up" />
                  </button>
                </div>
              </footer>
            </section>
          </article>
        </template>
      </template>
    </main>

    <van-image-preview v-model="imagePreviewOpen" :images="previewImages" :start-position="previewImageIndex" :close-on-popstate="false"
      get-container="body" class-name="qa-material-preview" role="dialog" aria-label="物料图片预览" aria-modal="true" @close="closeMaterialPreview" @keydown.esc.native.stop.prevent="closeMaterialPreview">
      <template slot="cover">
        <button ref="imagePreviewClose" type="button" class="material-preview-close" aria-label="关闭图片预览" @click.stop="closeMaterialPreview">关闭</button>
      </template>
    </van-image-preview>
  </div>
</template>

<script>
import { getDate, getDate2 } from '@/api/tools'
import { getMaterialImageUrls, materialImagePath } from '@/api/materialImages'

const QUERY_URL = 'xuper_auth/getBaseData'
const UPDATE_URL = 'xuper_auth/updateRec'
const PERMISSION_FORM_ID = '493932'
const FIELD_FORM_ID = '450125'

function literal(value) {
  return "'" + String(value == null ? '' : value).replace(/'/g, "''") + "'"
}

function likeLiteral(value) {
  return literal('%' + String(value).toLowerCase().replace(/[\\%_]/g, '\\$&') + '%') + " escape '\\'"
}

function precision(value) {
  const number = Number(value)
  return value !== null && value !== '' && Number.isInteger(number) && number >= 0 && number <= 12 ? number : 6
}

function quantity(value, label) {
  if ((typeof value !== 'string' && typeof value !== 'number') || !/^\d*\.?\d+$/.test(String(value).trim())) {
    throw new Error(label + '必须填写有效的非负数')
  }
  const result = Number(value)
  if (!Number.isFinite(result) || result < 0) throw new Error(label + '必须填写有效的非负数')
  return result
}

function round(value, digits) {
  const factor = Math.pow(10, digits)
  return Math.round((value + Number.EPSILON * Math.abs(value)) * factor) / factor
}

function calculateQuantities(actual, deduction, config = {}) {
  const received = quantity(actual, '送货数量')
  const input = quantity(deduction, '质检不良数')
  const QCQTY = round(input, precision(config.qcPrecision))
  if (input > received || QCQTY > received) throw new Error('质检不良数不能大于送货数量')
  const STKQTY = round(received - QCQTY, precision(config.stkPrecision))
  if (!Number.isFinite(QCQTY) || !Number.isFinite(STKQTY)) throw new Error('数量超出有效范围')
  return { QCQTY, STKQTY }
}

function groupDetails(rows) {
  return rows.reduce((groups, row) => {
    const key = String(row.IDSEQ)
    if (!groups[key]) groups[key] = []
    groups[key].push(row)
    return groups
  }, Object.create(null))
}

function hasResult(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function errorText(response, fallback) {
  return typeof response.result === 'string' && response.result ? response.result : response.message || fallback
}

function dialogText(value) {
  const entities = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
  return String(value).replace(/[&<>"']/g, character => entities[character])
}

function normalizeFields(records) {
  return records.map(row => {
    const field = String(row.FLDNO || row.key || '').toUpperCase()
    return {
      field, title: String(row.title || row.TITLE || row.FLDNM || row.CNAME || field),
      type: String(row.EDCSS || '').toLowerCase(), disabled: row.DISABLED === 'Y',
      visible: row.THIDE !== 'Y', formVisible: row.FHIDE === 'N'
    }
  }).filter(row => /^[A-Z][A-Z0-9_]*$/.test(row.field))
}

const clone = value => JSON.parse(JSON.stringify(value))
const keyOf = row => String(row.IDSEQ) + ':' + String(row.LSTSEQ)
const emptyPermissions = () => ({ canView: false, canEdit: false, canConfirm: false, canDelete: false })

export default {
  name: 'PoStkchk',
  data() {
    return {
      search: '', appliedSearch: '', status: 'N',
      permissions: emptyPermissions(), permissionLoading: true, permissionError: '',
      editBool: true, cfmBool: true, delBool: true,
      allDocuments: [], summaries: {}, listLoading: false, listError: '', summaryLoading: false, summaryError: '',
      expandedIds: [],
      dictionary: { options: [], qcPrecision: 6, stkPrecision: 6, checkFields: [], detailFields: [], headerFields: [], summaryFields: [] }, dictionaryLoading: false, dictionaryError: '',
      busy: false, activeDocumentId: '', activeAction: '',
      imagePreviewOpen: false, previewImages: [], previewImageIndex: 0,
      materialImageSets: {}, materialImageLoading: {}, materialImageErrors: {}
    }
  },
  computed: {
    detailInputDisabled() { return this.busy || this.dictionaryLoading || !!this.dictionaryError || !this.dictionary.options.length },
    checkFields() { return (this.dictionary.checkFields || []).map(field => ({ ...field, title: field.title.replace(/\s*OK\s*$/i, '') })) }
  },
  created() {
    this._listSequence = 0
    this._summarySequence = 0
    this._popupSequence = 0
    this._activeQuery = null
    this._enterSearchHandled = false
    this._qaIdentity = {
      corpid: this.$cookies.get('v_corpid'), userId: this.$cookies.get('v_idseq'),
      userNo: this.$cookies.get('v_telno'), userName: this.$cookies.get('v_usernm')
    }
    this._qaReady = !!(this._qaIdentity.corpid && this._qaIdentity.userId && this._qaIdentity.userNo)
    this._qaTrustedCheckFields = []
    this._qaDetailMetadataLoaded = false
    this._qaFieldSettings = { qcPrecision: 6, stkPrecision: 6 }
    this._qaHeaderColumns = []
    this._qaHeaderColumnsLoaded = false
    this._qaHeaderColumnsRequest = null
    if (!this._qaReady) this.permissionError = '登录信息不完整，请重新登录'
  },
  mounted() { this.initialize() },
  beforeDestroy() {
    this._listSequence++
    this._summarySequence++
    this._popupSequence++
  },
  async beforeRouteLeave(to, from, next) {
    if (this.busy) { this.$toast('操作处理中，请稍候'); next(false); return }
    next(true)
  },
  methods: {
    rowKey(row) { return String(row.IDSEQ) + ':' + String(row.LSTSEQ) },
    displayValue(value) { return value === null || value === undefined || value === '' ? '—' : value },
    flagName(value) { return value === 'Y' ? '是' : value === 'N' ? '否' : '未勾选' },
    materialImageKey(row) {
      if (hasResult(row.MATNO)) return 'mat:' + row.MATNO
      const id = row.MATIDSEQ || row.MAT_IDSEQ
      return hasResult(id) ? 'id:' + id : 'line:' + this.rowKey(row)
    },
    materialImages(row) {
      const images = this.materialImageSets[this.materialImageKey(row)]
      if (images) return images
      const mainImage = materialImagePath(row.IMGPATH)
      return mainImage ? [mainImage] : []
    },
    async qaGetMaterialImages(row) {
      return getMaterialImageUrls(row, this._qaIdentity.corpid, (data, url) => this.qaRows(data, url))
    },
    async loadMaterialImages(rows, sequence = this._popupSequence) {
      const unique = new Map(rows.map(row => [this.materialImageKey(row), row]))
      await Promise.all(Array.from(unique, async ([key, row]) => {
        if (this.materialImageLoading[key]) return
        this.$set(this.materialImageLoading, key, true)
        this.$set(this.materialImageErrors, key, '')
        const isCurrent = () => sequence === this._popupSequence
        try {
          const images = await this.qaGetMaterialImages(row)
          if (isCurrent()) this.$set(this.materialImageSets, key, images)
        } catch (error) {
          if (isCurrent()) this.$set(this.materialImageErrors, key, error.message)
        } finally {
          if (isCurrent()) this.$set(this.materialImageLoading, key, false)
        }
      }))
    },
    previewMaterialImages(row, index, event) {
      const images = this.materialImages(row)
      if (!Number.isInteger(index) || index < 0 || index >= images.length) return
      this.previewImages = images
      this.previewImageIndex = index
      this._materialPreviewTrigger = event && event.currentTarget
      this.imagePreviewOpen = true
      this.$nextTick(() => { if (this.$refs.imagePreviewClose) this.$refs.imagePreviewClose.focus() })
    },
    closeMaterialPreview() {
      this.imagePreviewOpen = false
      const trigger = this._materialPreviewTrigger
      this._materialPreviewTrigger = null
      if (trigger && trigger.isConnected) this.$nextTick(() => trigger.focus())
    },
    toggleCheck(row, field) {
      if (!this.popupReadonly && !this.detailInputDisabled && !field.disabled) this.updateDraft(row, field.field, row[field.field] === 'Y' ? 'N' : 'Y')
    },
    fieldTitle(field, fallback) {
      const definition = (this.dictionary.detailFields || []).find(item => item.field === field)
      return definition ? definition.title : fallback
    },
    detailInformation(row) {
      const excluded = ['IDSEQ', 'LSTSEQ', 'RN', 'MATNO', 'MATNM', 'MATDESC', 'SPECNM', 'IMGPATH', 'ACTQTY', 'QCQTY', 'STKQTY', 'QCRESULT', 'QCRESULTNM', 'RMK', 'ORDNO']
      const configured = this.dictionary.detailFields || []
      if (configured.length) return configured.filter(field => field.type !== 'checkbox' && !excluded.includes(field.field) && !/销售订单|关联订单|送货单号|送货备注|新增|修改|审核/.test(field.title))
      return [{ field: 'QTY', title: '采购数量' }, { field: 'UNITNM', title: '采购单位' }]
        .filter(field => Object.prototype.hasOwnProperty.call(row, field.field))
    },
    calculation(row) {
      if (row.QCQTY === '' || row.QCQTY === null || row.QCQTY === undefined) {
        return { values: null, error: '' }
      }
      try { return { values: this.calculateQuantities(row.ACTQTY, row.QCQTY, this.dictionary), error: '' } }
      catch (error) { return { values: null, error: error.message } }
    },
    calculateQuantities,
    groupDetails,

    // 当前页面的查询、权限和写入请求统一通过 this.$axios。
    async qaPost(url, data) {
      const response = await this.$axios({ method: 'post', url, data })
      if (!response || !hasResult(response.code)) throw new Error('服务器响应不完整，操作结果需要核对')
      if (String(response.code) !== '200') {
        const error = new Error(errorText(response || {}, '请求失败，请重试'))
        error.businessError = true
        throw error
      }
      return response
    },

    async qaRows(data, url = QUERY_URL) {
      const response = await this.qaPost(url, data)
      if (!Array.isArray(response.result)) throw new Error('查询返回格式异常，请重试')
      return response.result
    },

    qaScope(table, condition) {
      return { p_table: table, p_key: 'corpid', p_value: this._qaIdentity.corpid, p_key2: 'idty', p_value2: 'QACHK', p_cond: condition }
    },

    async qaGetPermissions() {
      const records = await this.qaRows({
        p_table: 'v_sys_user_priv', p_key: 'userno', p_value: this._qaIdentity.userNo,
        p_key2: 'frmidseq', p_value2: PERMISSION_FORM_ID
      })
      const selected = records.filter(row => row.ISSEL === 'Y')
      return {
        canView: selected.length > 0,
        canEdit: selected.some(row => row.ISUPD === 'Y'),
        canConfirm: selected.some(row => row.ISCFM === 'Y'),
        canDelete: selected.some(row => row.ISDEL === 'Y')
      }
    },

    async qaRequirePermission(key) {
      const permissions = await this.qaGetPermissions()
      if (!permissions.canView || !permissions[key]) throw new Error('当前账号没有此操作权限')
    },

    qaLoadHeaderColumns(refresh = false) {
      if (this._qaHeaderColumnsRequest) return this._qaHeaderColumnsRequest
      if (this._qaHeaderColumnsLoaded && !refresh) return Promise.resolve(this._qaHeaderColumns)
      this._qaHeaderColumnsLoaded = false
      const request = this.qaRows({ p_table: 'v_sys_form_fld', p_key: 'idseq', p_value: FIELD_FORM_ID, p_key2: 'lstseq', p_value2: '1', p_cond: ' order by sortby,lstseq' }, 'xuper_auth/getTableRecMany')
        .then(records => { this._qaHeaderColumns = normalizeFields(records); this._qaHeaderColumnsLoaded = true; return this._qaHeaderColumns })
      this._qaHeaderColumnsRequest = request
      const clear = () => { if (this._qaHeaderColumnsRequest === request) this._qaHeaderColumnsRequest = null }
      request.then(clear, clear)
      return request
    },

    async qaGetDictionary() {
      this._qaDetailMetadataLoaded = false
      this._qaTrustedCheckFields = []
      const [fields] = await Promise.all([
        this.qaRows({ p_table: 'v_sys_form_fld', p_key: 'idseq', p_value: FIELD_FORM_ID, p_key2: 'lstseq', p_value2: '2', p_cond: ' order by sortby,lstseq' }, 'xuper_auth/getTableRecMany'),
        this.qaLoadHeaderColumns(true)
      ])
      const findField = name => fields.find(row => String(row.FLDNO || row.key || '').toUpperCase() === name) || {}
      const resultField = findField('QCRESULT')
      if (!resultField.COMBO) throw new Error('未配置质检结果字典，请联系管理员')
      const records = await this.qaRows({ p_table: 'v_sys_base', p_key: 'parentno', p_value: resultField.COMBO })
      const options = records.filter(row => hasResult(row.CODE) && hasResult(row.CNAME)).map(row => ({ CODE: row.CODE, CNAME: row.CNAME }))
      if (!options.length) throw new Error('质检结果字典为空，请联系管理员')
      const detailFields = normalizeFields(fields).filter(row => row.visible)
      const reserved = ['IDSEQ', 'LSTSEQ', 'CORPID', 'IDTY', 'STFG', 'ACTQTY', 'QTY', 'QCQTY', 'STKQTY', 'QCRESULT', 'RMK']
      this._qaTrustedCheckFields = detailFields.filter(row => row.type === 'checkbox' && !reserved.includes(row.field)).map(row => ({ ...row }))
      this._qaFieldSettings = { qcPrecision: precision(findField('QCQTY').DOTNUM), stkPrecision: precision(findField('STKQTY').DOTNUM) }
      this._qaDetailMetadataLoaded = true
      return {
        options, qcPrecision: precision(findField('QCQTY').DOTNUM), stkPrecision: precision(findField('STKQTY').DOTNUM),
        detailFields, checkFields: this._qaTrustedCheckFields.map(row => ({ ...row })), headerFields: this._qaHeaderColumns.filter(row => row.formVisible),
        summaryFields: this._qaHeaderColumns.filter(row => row.visible)
      }
    },

    async qaGetDocuments({ status = 'N', search = '' } = {}) {
      if (!['N', 'Y'].includes(status)) throw new Error('无效的单据状态')
      let condition = " and icdttm >= '" + getDate2(30) + "'"
      condition += ' and stfg=' + literal(status)
      const keyword = String(search).trim()
      if (keyword) {
        await this.qaLoadHeaderColumns()
        const pattern = likeLiteral(keyword)
        const purchaseFields = this._qaHeaderColumns.filter(row => /采购.*(单号|编号|编码)/.test(row.title) && !['IDNO', 'SUPNM'].includes(row.field))
        const purchaseSearch = purchaseFields.map(row => ' or lower(' + row.field.toLowerCase() + ') like ' + pattern).join('')
        condition += ' and (lower(idno) like ' + pattern + ' or lower(supnm) like ' + pattern + purchaseSearch +
          ' or idseq in (select idseq from V_IC_STKDTL_QA where corpid=' + literal(this._qaIdentity.corpid) +
          " and idty='QACHK' and (lower(ordno) like " + pattern + ' or lower(matno) like ' + pattern + ')))'
      }
      const records = await this.qaRows(this.qaScope('V_IC_STK_QA', condition + ' order by icdttm desc,idseq desc'))
      return records.filter(row => row.STFG === status)
    },

    async qaGetDocument(id) {
      if (!hasResult(id)) throw new Error('缺少单据标识')
      const records = await this.qaRows(this.qaScope('V_IC_STK_QA', ' and idseq=' + literal(id)))
      const row = records.find(item => String(item.IDSEQ) === String(id) && String(item.CORPID) === String(this._qaIdentity.corpid) && item.IDTY === 'QACHK')
      if (!row) throw new Error('单据不存在或不属于当前组织')
      return row
    },

    qaGetDetails(ids) {
      const unique = Array.from(new Set(ids.filter(hasResult).map(String)))
      if (!unique.length) return Promise.resolve([])
      return this.qaRows(this.qaScope('V_IC_STKDTL_QA', ' and idseq in (' + unique.map(literal).join(',') + ') order by idseq,lstseq'))
    },



    async qaSaveDocument(document, drafts) {
      if (!Array.isArray(drafts) || !drafts.length) throw new Error('没有可保存的质检明细')
      const sequence = row => String(row.LSTSEQ)
      const seen = new Set()
      drafts.forEach((row, index) => {
        if (String(row.IDSEQ) !== String(document.IDSEQ) || !hasResult(row.LSTSEQ) || seen.has(sequence(row))) throw new Error('明细标识重复或与当前单据不匹配')
        seen.add(sequence(row))
        if (!hasResult(row.QCRESULT)) throw new Error('第 ' + (index + 1) + ' 项：请选择质检结果')
      })
      await this.qaRequirePermission('canEdit')
      if (!this._qaDetailMetadataLoaded) await this.qaGetDictionary()
      const [current, originals] = await Promise.all([this.qaGetDocument(document.IDSEQ), this.qaGetDetails([document.IDSEQ])])
      if (current.STFG !== 'N') throw new Error('单据状态已变化，当前不能保存')
      if (originals.length !== drafts.length || originals.some(row => String(row.IDSEQ) !== String(document.IDSEQ) || !seen.has(sequence(row)))) throw new Error('明细记录已变化，请重新打开单据后录入')
      const expected = drafts.map((draft, index) => {
        const original = originals.find(row => sequence(row) === sequence(draft))
        if (!original) throw new Error('明细记录已变化，请重新打开单据后录入')
        if (Number(draft.ACTQTY) !== Number(original.ACTQTY)) throw new Error('第 ' + (index + 1) + ' 项送货数量已变化，请重新打开单据后录入')
        let values
        try { values = calculateQuantities(original.ACTQTY, draft.QCQTY, this._qaFieldSettings) }
        catch (error) { throw new Error('第 ' + (index + 1) + ' 项：' + error.message) }
        const flags = {}
        this._qaTrustedCheckFields.filter(field => !field.disabled).forEach(field => {
          const value = draft[field.field]
          if (value === null || value === undefined) return
          if (value !== 'Y' && value !== 'N') throw new Error(field.title + '必须为勾选或未勾选')
          flags[field.field] = value
        })
        // Preserve required source columns exactly as the PC page's existing batch contract does.
        // Only QA inputs from the editor are merged into these freshly read rows.
        return { row: { ...original, QCRESULT: String(draft.QCRESULT).trim(), ...values, RMK: String(draft.RMK == null ? '' : draft.RMK), ...flags }, flags }
      })
      const latest = await this.qaGetDocument(document.IDSEQ)
      if (latest.STFG !== 'N') throw new Error('单据状态已变化，当前不能保存')

      const verifySaved = async () => {
        const [parent, details] = await Promise.all([this.qaGetDocument(document.IDSEQ), this.qaGetDetails([document.IDSEQ])])
        if (parent.STFG !== 'N') throw new Error('单据状态已变化，请刷新核对统一保存结果')
        if (details.length !== expected.length) throw new Error('明细记录数量不一致，请重新核对')
        for (const { row, flags } of expected) {
          const persisted = details.find(item => String(item.IDSEQ) === String(document.IDSEQ) && sequence(item) === sequence(row))
          if (!persisted || Number(persisted.ACTQTY) !== Number(row.ACTQTY) || !hasResult(persisted.QCQTY) || !hasResult(persisted.STKQTY) ||
              String(persisted.QCRESULT).trim() !== row.QCRESULT || Number(persisted.QCQTY) !== row.QCQTY || Number(persisted.STKQTY) !== row.STKQTY ||
              String(persisted.RMK || '') !== row.RMK || Object.keys(flags).some(field => String(persisted[field]).trim() !== flags[field])) {
            throw new Error('明细回读结果与提交内容不一致，统一保存未生效或已被修改')
          }
        }
        return { document: parent, details }
      }
      try {
        // One batch request. Transaction locking and rollback belong to this existing backend endpoint.
        await this.qaPost('xuper_auth/batDtlSave2', {
          p_table: 'IC_STKDTL', p_idseq: document.IDSEQ, p_action: 'S', p_user: this._qaIdentity.userNo,
          p_json: JSON.stringify(expected.map(item => item.row))
        })
      } catch (error) {
        if (error.businessError) throw error
        try { return await verifySaved() }
        catch (readError) { throw new Error('统一保存响应异常，请刷新核对；当前录入已保留：' + readError.message + '；' + error.message) }
      }
      try { return await verifySaved() }
      catch (error) { throw new Error('统一保存结果核对未完成，请刷新确认：' + error.message) }
    },

    async qaChangeStatus(document, target) {
      if (!['N', 'Y', 'X'].includes(target)) throw new Error('无效的单据操作')
      if (target === 'Y' && !hasResult(this._qaIdentity.userName)) throw new Error('缺少审核人姓名，请重新登录')
      await this.qaRequirePermission(target === 'X' ? 'canDelete' : 'canConfirm')
      const expected = target === 'N' ? 'Y' : 'N'
      const current = await this.qaGetDocument(document.IDSEQ)
      if (current.STFG !== expected) throw new Error('单据状态已变化，请刷新后操作')
      if (target === 'Y') {
        const details = await this.qaGetDetails([current.IDSEQ])
        if (!details.length) throw new Error('没有质检明细，不能审核')
        if (details.some(row => !hasResult(row.QCRESULT))) throw new Error('明细质检结果未全部录入，不能审核')
        if (details.some(row => !hasResult(row.QCQTY))) throw new Error('明细质检不良数未全部录入，不能审核；无不良请填写 0')
      }
      const v_dttm = target === 'Y' ? getDate('full') : ''
      const v_cond = target === 'Y'
        ? ' set stfg=\'' + target + '\',cHKdttm=\'' + v_dttm + '\',CHKUSER=\'' + this._qaIdentity.userName + '\''
        : ' set stfg=\'' + target + '\',cHKdttm=null,CHKUSER=null'
      try {
        await this.qaPost(UPDATE_URL, {
          p_key: 'idseq',
          p_value: String(current.IDSEQ),
          p_table: 'ic_stk',
          p_cond: v_cond
        })
      } catch (error) {
        if (error.businessError) throw error
        const actual = await this.qaGetDocument(current.IDSEQ).catch(() => null)
        if (actual && actual.STFG === target) {
          return { document: actual, reportError: new Error(target === 'Y'
            ? '当前单据已审核；审核响应异常，报告生成尚未执行，请核实'
            : '当前单据已' + (target === 'X' ? '作废' : '弃审') + '；请求响应异常，已回读确认状态') }
        }
        throw new Error('操作响应异常，已尝试核对单据状态，请刷新确认：' + error.message)
      }
      let reportError = null
      if (target === 'Y') {
        try {
          // 仅审核成功后生成质检单；弃审和作废不执行。
          await this.qaPost('/xuper_oss/call_package', {
            corpid: this._qaIdentity.corpid,
            p_idseq: current.IDSEQ,
            p_action: 'QA_CHECK#',
            p_user: this._qaIdentity.userNo,
            p_cond: target
          })
          await this.qaPost('/xuper_oss/call_package', { corpid: this._qaIdentity.corpid, p_idseq: current.IDSEQ, p_action: 'QACHK_IN#', p_user: this._qaIdentity.userNo })
        } catch (error) {
          reportError = new Error((error.businessError ? '报告生成失败：' : '报告生成结果未确认：') + error.message)
        }
      }
      // Display refresh must not prevent the process after a confirmed status update.
      let actual = { ...current, STFG: target }
      let refreshError = null
      try {
        actual = await this.qaGetDocument(current.IDSEQ)
        if (actual.STFG !== target) refreshError = new Error('单据状态已再次变化，请刷新核实')
      } catch (error) { refreshError = new Error('状态更新已提交，但回读失败，请刷新核实：' + error.message) }
      return { document: actual, reportError, refreshError }
    },

    async initialize() {
      if (!this._qaReady) { this.$router.replace('/login'); return }
      this.permissionLoading = true
      this.permissionError = ''
      this.permissions = emptyPermissions()
      this.editBool = this.cfmBool = this.delBool = true
      try {
        const permissions = await this.qaGetPermissions()
        this.permissions = permissions
        this.editBool = !permissions.canEdit
        this.cfmBool = !permissions.canConfirm
        this.delBool = !permissions.canDelete
        if (permissions.canView) await Promise.all([this.loadDictionary(), this.onSearch()])
      } catch (error) { this.permissionError = error.message }
      finally { this.permissionLoading = false }
    },
    async loadDictionary() {
      if (this.dictionaryLoading) return
      this.dictionaryLoading = true
      this.dictionaryError = ''
      try { this.dictionary = await this.qaGetDictionary() }
      catch (error) { this.dictionary.options = []; this.dictionaryError = error.message }
      finally { this.dictionaryLoading = false }
    },
    resetSearchEnter() { this._enterSearchHandled = false },
    onSearchEnter(event) {
      if (this.busy || this.permissionLoading || !this.permissions.canView || event.isComposing || event.keyCode === 229 || event.repeat) return
      if (event.type === 'keyup' && this._enterSearchHandled) {
        this._enterSearchHandled = false
        return
      }
      this._enterSearchHandled = event.type === 'keydown'
      // Some PDA scanners update the input immediately before sending Enter.
      if (event.target && typeof event.target.value === 'string') this.search = event.target.value
      return this.onSearch()
    },
    onSearch() {
      if (this.busy || !this.permissions.canView) return Promise.resolve()
      this.appliedSearch = this.search.trim()
      return this.loadDocuments(true)
    },
    clearSearch() { this.search = ''; return this.onSearch() },
    loadDocuments(force = false) {
      if (!this.permissions.canView) return Promise.resolve()
      const query = { status: 'N', search: this.appliedSearch }
      const signature = JSON.stringify(query)
      if (!force && this._activeQuery && this._activeQuery.signature === signature) return this._activeQuery.promise
      const sequence = ++this._listSequence
      this._summarySequence++
      this.summaryLoading = false
      this.listLoading = true
      this.listError = ''
      const promise = (async () => {
        try {
          const documents = await this.qaGetDocuments(query)
          if (sequence !== this._listSequence) return
          this.allDocuments = documents
          this.listLoading = false
          await this.loadSummaries()
        } catch (error) {
          if (sequence === this._listSequence) { this.listError = error.message; this.allDocuments = [] }
        } finally {
          if (sequence === this._listSequence) { this.listLoading = false; this._activeQuery = null }
        }
      })()
      this._activeQuery = { signature, promise }
      return promise
    },
    async loadSummaries() {
      const sequence = ++this._summarySequence
      this.summaryLoading = true
      this.summaryError = ''
      this.summaries = {}
      try {
        const rows = await this.qaGetDetails(this.allDocuments.map(row => row.IDSEQ))
        if (sequence === this._summarySequence) {
          // 清空待质检单据中后台可能带出的默认不良数与合格数，强制由用户输入
          rows.forEach(r => {
            r.QCQTY = ''
            r.STKQTY = ''
          })
          const groups = this.groupDetails(rows)
          // 深度复制明细，支持就地编辑
          this.summaries = clone(groups)
        }
      } catch (error) { if (sequence === this._summarySequence) this.summaryError = error.message }
      finally { if (sequence === this._summarySequence) this.summaryLoading = false }
    },
    detailsFor(row) { return this.summaries[String(row.IDSEQ)] || [] },
    isExpanded(row) {
      return this.expandedIds.includes(String(row.IDSEQ))
    },
    toggleExpand(row) {
      const id = String(row.IDSEQ)
      const index = this.expandedIds.indexOf(id)
      if (index >= 0) {
        this.expandedIds.splice(index, 1)
      } else {
        this.expandedIds.push(id)
        const details = this.detailsFor(row)
        if (details.length) {
          this.loadMaterialImages(details)
        }
      }
    },
    updateDetailDraft(doc, row, field, value) {
      const checkField = (this.dictionary.checkFields || []).find(item => item.field === field && !item.disabled)
      if (this.busy || (!['QCRESULT', 'QCQTY', 'RMK'].includes(field) && !checkField)) return
      if (checkField && !['Y', 'N'].includes(value)) return
      this.$set(row, field, value)
    },
    toggleInlineCheck(doc, row, field) {
      if (!this.detailInputDisabled && !field.disabled) {
        this.updateDetailDraft(doc, row, field.field, row[field.field] === 'Y' ? 'N' : 'Y')
      }
    },
    calcDocumentTotals(doc) {
      const result = {}
      const rows = this.detailsFor(doc)
      for (const field of ['ACTQTY', 'QCQTY', 'STKQTY']) {
        const values = rows.map(row => {
          if (field === 'QCQTY') {
            return row.QCQTY
          }
          if (field === 'STKQTY') {
            const calc = this.calculation(row)
            return calc.values ? calc.values.STKQTY : null
          }
          return row[field]
        }).filter(value => value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value)))
        result[field] = values.length ? Number(values.reduce((sum, value) => sum + Number(value), 0).toFixed(6)) : '—'
      }
      return result
    },
    async submitAudit(doc) {
      if (this.busy || this.cfmBool || !this.permissions.canView || doc.STFG !== 'N') return
      const details = this.detailsFor(doc)
      if (!details || !details.length) {
        this.$dialog.alert({ title: '提示', message: '没有质检明细，不能审核' })
        return
      }

      // 1. 校验质检结果与不良数
      for (let i = 0; i < details.length; i++) {
        const line = details[i]
        const num = i + 1
        if (!hasResult(line.QCRESULT)) {
          this.$dialog.alert({
            title: '质检提示',
            confirmButtonColor: '#235EFF',
            message: `第 ${num} 项明细（${line.MATNM || line.MATNO || ''}）未选择质检结果，请先完善`
          })
          if (!this.isExpanded(doc)) this.toggleExpand(doc)
          return
        }
        if (!hasResult(line.QCQTY)) {
          this.$dialog.alert({
            title: '质检提示',
            confirmButtonColor: '#235EFF',
            message: `第 ${num} 项明细（${line.MATNM || line.MATNO || ''}）未填写质检不良数（无不良请填 0）`
          })
          if (!this.isExpanded(doc)) this.toggleExpand(doc)
          return
        }
        const calc = this.calculation(line)
        if (calc.error) {
          this.$dialog.alert({
            title: '质检提示',
            confirmButtonColor: '#235EFF',
            message: `第 ${num} 项明细数据有误：${calc.error}`
          })
          if (!this.isExpanded(doc)) this.toggleExpand(doc)
          return
        }
      }

      // 2. 确认提示
      try {
        await this.$dialog.confirm({
          title: '质检确认',
          message: '确定保存并质检单据 ' + doc.IDNO + ' 吗？',
          confirmButtonColor: '#235EFF',
          confirmButtonText: '确认质检',
          cancelButtonText: '取消'
        })
      } catch (e) {
        return
      }

      this.busy = true
      this.activeDocumentId = String(doc.IDSEQ)
      this.activeAction = 'Y'

      try {
        // 3. 先自动保存明细（batDtlSave2）
        await this.qaSaveDocument(doc, clone(details))

        // 4. 紧接着自动触发审核
        const result = await this.qaChangeStatus(doc, 'Y')

        this.$toast.success('质检成功')
        if (result.reportError || result.refreshError) {
          this.$dialog.alert({
            title: '单据处理结果',
            confirmButtonColor: '#235EFF',
            message: doc.IDNO + '：' + [result.reportError, result.refreshError].filter(Boolean).map(error => error.message).join('；')
          })
        }

        // 5. 质检成功后刷新列表并移除展开状态
        const expIdx = this.expandedIds.indexOf(String(doc.IDSEQ))
        if (expIdx >= 0) this.expandedIds.splice(expIdx, 1)
        await this.loadDocuments(true)
      } catch (error) {
        this.$dialog.alert({ title: '质检未完成', confirmButtonColor: '#235EFF', message: error.message })
        await this.loadDocuments(true)
      } finally {
        this.busy = false
        this.activeDocumentId = ''
        this.activeAction = ''
      }
    },
    purchaseNumbers(row) {
      const headerField = (this.dictionary.headerFields || []).find(field => /采购.*(单号|编号|编码)/.test(field.title))
      if (headerField && row[headerField.field]) return row[headerField.field]
      if (row.REFPONO || row.PONO) return row.REFPONO || row.PONO
      const numbers = Array.from(new Set(this.detailsFor(row).map(line => line.PONO || line.REFPONO).filter(Boolean)))
      return numbers.length ? numbers.join('、') : '—'
    },
    summaryQuantity(row, field) {
      const titles = { ACTQTY: '送货数量', QCQTY: '质检不良数', STKQTY: '质检合格数', INQTY: '入库数量', RTNQTY: '退货数' }
      const configured = (this.dictionary.summaryFields || []).find(item => item.title.replace(/[\s*]/g, '') === titles[field])
      if (configured && row[configured.field] !== null && row[configured.field] !== undefined && row[configured.field] !== '') return row[configured.field]
      if (row[field] !== null && row[field] !== undefined && row[field] !== '') return row[field]
      const values = this.detailsFor(row).map(line => {
        if (field === 'STKQTY') {
          const calc = this.calculation(line)
          return calc.values ? calc.values.STKQTY : null
        }
        return line[field]
      }).filter(value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)))
      if (!values.length) return field === 'ACTQTY' ? this.displayNumber(row.QTY) : '—'
      return Number(values.reduce((total, value) => total + Number(value), 0).toFixed(6))
    },
    displayNumber(value, fallback) { return value !== null && value !== undefined && value !== '' ? value : fallback !== null && fallback !== undefined && fallback !== '' ? fallback : '—' },
    resultName(row) {
      const option = this.dictionary.options.find(item => String(item.CODE) === String(row.QCRESULT))
      return row.QCRESULTNM || (option && option.CNAME) || row.QCRESULT || '—'
    },
    statusText(row) {
      return row.STFG === 'Y' ? '已质检' : row.STFG === 'N' ? '待质检' : ''
    },
    statusClass(status) { return status === 'Y' ? 'is-audited' : status === 'N' ? 'is-pending' : '' },
    onClickLeft() { if (!this.busy) this.$router.push('/features') }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/css/scss/variables.scss';

.po-stkchk { height: 100vh; height: 100dvh; display: flex; flex-direction: column; background: #F7F8FA; color: $text-primary; font: 14px/1.45 $font-family-base; }
.po-stkchk ::v-deep .van-button { border: 0; }
.po-stkchk ::v-deep .van-button:not(.van-button--small) { height: 40px; line-height: 38px; border-radius: 6px; font-size: 14px; }
.po-stkchk ::v-deep .van-button--disabled { background: $color-disabled !important; color: white !important; opacity: 1; }

.query-header { flex: none; display: flex; align-items: center; gap: 6px; min-height: 48px; padding: 4px 8px; background: white; border-bottom: 1px solid #EBEDF0; }
.back-button { display: flex; align-items: center; justify-content: center; flex: none; width: 36px; height: 36px; padding: 0; border: 0; background: transparent; color: $text-primary; }
.back-button:disabled { color: $text-tertiary; }
.search-form { display: flex; align-items: center; flex: 1; min-width: 0; gap: 6px; }
.search-input { min-width: 0; flex: 1; height: 32px; display: flex; align-items: center; gap: 6px; padding: 0 10px; border: 1px solid $border-color; border-radius: 16px; background: #F7F8FA; color: $text-secondary; }
.search-input:focus-within { border-color: $primary-color; background: white; }
.search-input input { width: 0; min-width: 0; flex: 1; height: 30px; outline: none; background: transparent; font: inherit; font-size: 13px; color: $text-primary; }
.search-input input::-webkit-search-cancel-button { display: none; }
.clear-button { width: 28px; height: 28px; flex: none; border: 0; background: transparent; color: $text-secondary; }

.header-action-button { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex: none; padding: 0; border: 1px solid #EBEDF0; border-radius: 6px; background: white; color: $text-primary; box-shadow: 0 1px 3px rgba(0,0,0,.06); }
.header-action-button:active { background: #F0F2F5; }
.header-action-button:disabled { color: $text-tertiary; box-shadow: none; }
.is-spinning { animation: spin 1s linear infinite; }
@keyframes spin { 100% { transform: rotate(360deg); } }

.list-content { flex: 1; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding: 8px; }
.state-panel { min-height: 180px; padding: 28px 12px; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 12px; text-align: center; color: $text-secondary; overflow-wrap: anywhere; }

.qa-card { max-width: 760px; margin: 0 auto 8px; padding: 10px 12px; border: 1px solid #EBEDF0; border-radius: 6px; background: white; }
.card-header { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px; }
.card-header h2 { min-width: 0; margin: 0; font-size: 15px; line-height: 22px; font-weight: 600; overflow-wrap: anywhere; }
.status-tag { flex: none; border-radius: 4px; padding: 0 6px; font-size: 12px; line-height: 20px; }
.is-audited { background: $color-action1; color: #153B2C; }
.is-pending { background: $auxiliary-color; color: $text-primary; }

.document-info { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 4px 10px; margin: 0; font-size: 13px; }
.document-info > div { display: grid; grid-template-columns: 48px minmax(0, 1fr); align-items: baseline; gap: 6px; min-width: 0; }
.document-info dt, .quantity-grid dt { font-size: 12px; color: $text-secondary; }
.document-info dd, .quantity-grid dd { margin: 0; overflow-wrap: anywhere; }
.full-row { grid-column: 1 / -1; }

.quantity-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin: 0; }
.quantity-grid dt { font-size: 12px; }
.quantity-grid dd { font-weight: 600; font-variant-numeric: tabular-nums; }
.document-quantities { margin-top: 8px; padding: 6px 8px; border-radius: 4px; background: #F7F8FA; }
.audit-info, .summary-state { padding-top: 6px; font-size: 12px; color: $text-secondary; }

.card-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 6px 8px; margin-top: 8px; }
.expanded-footer { margin-top: 12px; padding-top: 8px; border-top: 1px dashed #EBEDF0; }
.detail-count { margin: 0; font-size: 12px; color: $text-secondary; }
.card-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; margin-left: auto; }
.confirm-button { background: $color-action1; color: #153B2C; font-weight: 500; }
.confirm-button:active { background: $color-action1-active; }

.toggle-expand-btn { display: inline-flex; align-items: center; gap: 2px; height: 30px; padding: 0 8px; border: 1px solid #DCDFE6; border-radius: 4px; background: #F8F9FA; color: $primary-color; font-size: 13px; }
.toggle-expand-btn:active { background: #EBEDF0; }

.expanded-details { margin-top: 10px; padding-top: 10px; border-top: 1px dashed #DCDFE6; }
.detail-card { margin: 0 0 6px; padding: 6px 8px; border: 1px solid #EBEDF0; border-radius: 6px; background: #FAFAFB; }
.line-heading { display: flex; gap: 6px; align-items: flex-start; }
.line-heading h3 { flex: 1; min-width: 0; font-size: 13px; font-weight: 600; line-height: 20px; margin: 0; overflow-wrap: anywhere; }
.material-code { margin-right: 6px; }
.line-number { width: 20px; height: 20px; line-height: 20px; flex: none; border-radius: 3px; text-align: center; background: #EDF2FF; color: $primary-color; font-size: 11px; }
.line-meta { margin: 2px 0 4px; color: $text-secondary; font-size: 12px; overflow-wrap: anywhere; }
.line-info { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 2px 8px; margin: 4px 0 6px; font-size: 12px; }
.line-info > div { display: grid; grid-template-columns: 56px minmax(0, 1fr); align-items: baseline; gap: 4px; min-width: 0; }
.line-info dt { font-size: 12px; color: $text-secondary; }
.line-info dd { margin: 0; overflow-wrap: anywhere; }
.quantity-value { font-weight: 600; font-variant-numeric: tabular-nums; }
.wide-info { grid-column: 1 / -1; }

.inspection-inputs { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; border-top: 1px solid #EBEDF0; padding-top: 6px; margin-bottom: 4px; }
.result-field, .deduction-field { display: grid; grid-template-columns: max-content minmax(0, 1fr); align-items: center; gap: 4px; min-width: 0; }
.inspection-inputs label { margin: 0; font-size: 12px; white-space: nowrap; }
.required { color: $color-reject; }
.inspection-inputs select, .qa-quantity-input { display: block; width: 100%; min-width: 0; height: 30px; padding: 0 6px; border: 1px solid $border-color; border-radius: 4px; outline: none; background: white; color: $text-primary; font: inherit; font-size: 12px; }
.inspection-inputs select:focus, .qa-quantity-input:focus { border-color: $primary-color; }
.inspection-inputs select:disabled, .qa-quantity-input:disabled { background: #F7F8FA; color: $text-tertiary; opacity: 1; }

.stock-preview { display: flex; justify-content: space-between; align-items: center; gap: 6px; min-height: 28px; padding: 2px 8px; border-radius: 4px; background: #EDF2FF; font-size: 12px; margin-bottom: 4px; }
.stock-preview strong { font-size: 13px; font-weight: 600; color: $primary-color; overflow-wrap: anywhere; }
.quantity-error { margin: 2px 0 4px; color: #B83408; font-size: 11px; }

.qa-checks { display: flex; align-items: center; justify-content: space-between; gap: 4px; min-width: 0; padding: 2px 0; margin: 4px 0 0; border: 0; border-top: 1px solid #EBEDF0; }
.qa-checks legend { padding: 0 6px 0 0; color: $text-secondary; font-size: 11px; }
.qa-checks ::v-deep .van-checkbox { flex: none; min-height: 26px; min-width: 0; }
.qa-checks ::v-deep .van-checkbox__icon { height: 14px; font-size: 14px; flex-shrink: 0; }
.qa-checks ::v-deep .van-checkbox__label { margin-left: 3px; font-size: 11px; color: $text-primary; line-height: 18px; white-space: nowrap; }

.remark-field { display: flex; align-items: center; gap: 6px; margin-top: 3px; background: transparent; padding: 2px 0; }
.remark-field ::v-deep .van-field__label { width: 32px; margin-right: 4px; font-size: 12px; color: $text-secondary; }
.remark-field ::v-deep .van-field__control { min-height: 26px; height: 26px; padding: 2px 6px; border: 1px solid $border-color; border-radius: 4px; font-size: 12px; background: white; line-height: 22px; }

.material-images { display: grid; grid-template-columns: repeat(auto-fill, 48px); gap: 6px; margin-top: 8px; }
.material-image-status { display: flex; align-items: center; gap: 8px; margin: 6px 0 0; font-size: 12px; color: $text-secondary; }
.material-image-status button { min-width: 48px; height: 32px; padding: 0 4px; border: 0; background: transparent; color: $primary-color; }
.material-thumbnail { position: relative; display: block; min-width: 0; width: 100%; padding: 0; border: 1px solid #EBEDF0; border-radius: 4px; overflow: hidden; background: #F7F8FA; }
.material-thumbnail::before { display: block; padding-top: 100%; content: ''; }
.material-thumbnail ::v-deep .van-image { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
.material-image-error { font-size: 12px; color: $text-tertiary; }
.material-preview-close { position: fixed; top: calc(12px + env(safe-area-inset-top)); right: 12px; z-index: 1; min-width: 48px; height: 36px; padding: 0 12px; border: 0; border-radius: 18px; background: rgba(0,0,0,.5); color: white; font-size: 14px; }

.detail-totals { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; padding: 8px; margin: 8px 0 0; border-radius: 6px; background: #EDF2FF; }
.detail-totals dt { color: $text-secondary; font-size: 12px; }
.detail-totals dd { margin: 0; font-size: 15px; font-weight: 600; color: $primary-color; overflow-wrap: anywhere; }

.primary-button { background: $primary-color; color: white; }
.primary-button:active { background: $primary-active; }
.inline-notice { max-width: 760px; margin: 0 auto 12px; padding: 8px 12px; border-radius: 8px; background: #FFF6E8; color: #7D4400; font-size: 12px; overflow-wrap: anywhere; }
.inline-notice button { min-width: 44px; height: 44px; border: 0; background: transparent; color: $primary-color; }
button:focus-visible { outline: 2px solid $primary-color; outline-offset: 2px; }

.dictionary-notice { margin: 0 auto 8px; padding: 6px 10px; border-radius: 6px; background: #FFF6E8; color: #7D4400; overflow-wrap: anywhere; font-size: 12px; }
.dictionary-notice button { height: 32px; min-width: 64px; padding: 0 8px; border: 0; background: transparent; color: $primary-color; font-size: 13px; }

@media (max-width: 360px) {
  .inspection-inputs select, .qa-quantity-input { padding: 0 4px; }
  .card-footer .detail-count { width: 100%; }
}
</style>
